import SmoothScroll from "smooth-scroll";
import Navigation from "./lib/navigation";
import Accordion from "./lib/accordion";
import stickyContent from "./lib/stickyContent";
import { initTableScrollHint } from "./lib/tableScrollHint";
import GLightbox from "glightbox";
import Splide from "@splidejs/splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import initPieCharts from "./lib/pieCharts";
import initOpenCampusCalendar from "./lib/openCampusCalendar";
import initTabs from "./lib/tabs";
import accordion from "./lib/accordion";
import initLibraryCalendar from "./lib/libraryCalendar";

/* -------------------------------------------------------------------
  utility
------------------------------------------------------------------- */
const smoothScroll = new SmoothScroll('a[href*="#"]', {
  speed: 500,
  speedAsDuration: true,
  popstate: true,
  updateURL: false,
  offset: (anchor, toggle) => {
    if (toggle.href == "#") {
      return 0;
    } else {
      const headerHeight = document.querySelector(".header")
        ? document.querySelector(".header").offsetHeight
        : 0;
      return headerHeight;
    }
  },
});

initTableScrollHint();

Navigation("js-header-nav", "body");
stickyContent("footer", "header");

const galleryBox = GLightbox({
  selector: ".js-gallery-box",
});

const faqAccordion = new Accordion(".js-faq-item", {
  panelDisplay: "grid",
});

const headerNavAccordion = new Accordion(".js-nav-on", {
  breakpoint: 1366,
  breakpointType: "max",
  event: "click",
  panelDisplay: "flex",
  destroyDisplay: "reset",
});

const footerNavAccordion = new Accordion(".js-footer-nav-on", {
  breakpoint: 768,
  breakpointType: "max",
  event: "click",
  panelDisplay: "flex",
  disableLink: true,
});

const initOpenCampusSwitcher = () => {
  const switcher = document.querySelector(".js-open-campus-switcher");
  const itemsWrap = document.querySelector(".js-open-campus-switcher-items");
  if (!switcher || !itemsWrap) return;

  const buttons = Array.from(switcher.querySelectorAll(".wp-block-button"));
  const items = Array.from(itemsWrap.querySelectorAll(".post-item"));

  const filterItems = (dept) => {
    items.forEach((item) => {
      const itemDept = item.dataset.departmentItem || "";
      const shouldShow = !dept || itemDept === dept;
      item.classList.toggle("is-filter-hidden", !shouldShow);
    });
  };

  switcher.addEventListener("click", (event) => {
    const button = event.target.closest(".wp-block-button");
    if (!button || !switcher.contains(button)) return;
    event.preventDefault();

    buttons.forEach((btn) => {
      btn.classList.toggle("--is-active", btn === button);
    });

    filterItems(button.dataset.department || "");
  });

  const initial = buttons.find((btn) => btn.classList.contains("--is-active"));
  filterItems(initial?.dataset.department || "");
};

const initTopFvCarousel = () => {
  const slider = document.querySelector(".js-top-fv-carousel");
  if (!slider) return;

  const splide = new Splide(slider, {
    type: "loop",
    direction: "ttb",
    autoHeight: true,
    height: "var(--top-fv-height)",
    autoplay: true,
    interval: 5000,
    pauseOnHover: true,
    pagination: true,
    arrows: false,
    drag: true,
    wheel: true,
    releaseWheel: true,
    wheelSleep: 800,
    speed: 600,
    mediaQuery: "max",
    breakpoints: {
      1366: {
        direction: "ltr",
        autoHeight: true,
        height: "auto",
        width: "auto",
        gap: "1.25rem",
        padding: { left: "20vw", right: "20vw" },
      },
      599: {
        padding: { left: "10vw", right: "10vw" },
      },
    },
  });

  splide.mount();
};

const initPickupSlider = () => {
  const slider = document.querySelector(".js-pickup-open-campus-slider");
  if (!slider) return;

  const splide = new Splide(slider, {
    type: "slide",
    autoWidth: true,
    perMove: 1,
    pagination: false,
    arrows: true,
    focus: "center",
    trimSpace: false,
    gap: "var(--pickup-open-campus-gap)",
    speed: 600,
  });

  splide.mount();
};

const initCarouselBlocks = () => {
  const sliders = document.querySelectorAll(".js-carousel-slider");
  if (!sliders.length) return;

  sliders.forEach((slider) => {
    if (slider.dataset.carouselInitialized === "true") return;

    const toBool = (key) =>
      (slider.dataset[key] || "").toLowerCase() === "true";
    const autoSlide = toBool("autoslide");
    const showArrows = toBool("arrows");
    const showNavigation = toBool("navigation");

    const slideCount = slider.querySelectorAll(".splide__slide").length;

    const options = {
      type: autoSlide ? "loop" : "slide",
      arrows: showArrows,
      pagination: showNavigation,
      autoWidth: true,
      perMove: 1,
      drag: true,
      speed: autoSlide ? 1000 : 600,
      rewind: !autoSlide,
      autoplay: false,
      focus: "center",
      trimSpace: false,
    };

    let extensions = undefined;

    if (autoSlide) {
      options.autoScroll = {
        speed: 0.25,
        pauseOnHover: false,
        pauseOnFocus: false,
        rewind: false,
      };
      options.pauseOnHover = false;
      options.pauseOnFocus = false;
      options.waitForTransition = false;
      options.clones = Math.max(slideCount, 6);
      extensions = { AutoScroll };
    }

    const splide = new Splide(slider, options);

    if (extensions) {
      splide.mount(extensions);
    } else {
      splide.mount();
    }
    slider.dataset.carouselInitialized = "true";
  });
};

const isMp4Link = (anchor) => {
  const href = anchor.getAttribute("href") || "";
  if (!href) return false;

  try {
    const url = new URL(href, window.location.href);
    return url.pathname.toLowerCase().endsWith(".mp4");
  } catch (error) {
    return /\.mp4($|[?#])/i.test(href);
  }
};

const initVideoLightbox = () => {
  const candidates = Array.from(document.querySelectorAll("a[href]"));
  const videoLinks = candidates.filter((anchor) => isMp4Link(anchor));

  if (!videoLinks.length) return;

  videoLinks.forEach((anchor) => {
    if (!anchor.classList.contains("js-video-lightbox")) {
      anchor.classList.add("js-video-lightbox");
    }
    if (!anchor.dataset.type) {
      anchor.dataset.type = "video";
    }
  });

  GLightbox({
    selector: ".js-video-lightbox",
    touchNavigation: true,
    autoplayVideos: true,
  });
};

// viewport-without-scrollbar.js
const setViewportWidth = () => {
  const scrollbarWidth =
    window.innerWidth - document.documentElement.clientWidth;
  document.documentElement.style.setProperty(
    "--vw-without-scrollbar",
    `calc(100vw - ${scrollbarWidth}px)`,
  );
};

// top-fv-fade-slider
const topFvSlider = () => {
  const slider = document.querySelector(".js-top-fv");
  if (!slider) return;

  const splide = new Splide(slider, {
    type: "fade",
    rewind: true,
    autoplay: true,
    interval: 6000,
    pagination: false,
    arrows: false,
    pauseOnHover: false,
    pauseOnFocus: false,
    drag: false,
    swipe: false,
    keyboard: false,
  });

  const updateNextClass = () => {
    const slides = slider.querySelectorAll(".splide__slide");
    slides.forEach((s) => s.classList.remove("is-fv-next"));
    const total = splide.length;
    const current = splide.index;
    const nextIndex = (current + 1) % total;
    slides[nextIndex]?.classList.add("is-fv-next");
  };

  splide.on("mounted move", updateNextClass);
  splide.mount();
};

// 初回実行
setViewportWidth();

// ResizeObserverで継続監視
const observer = new ResizeObserver(() => {
  setViewportWidth();
});

// <html> のサイズ変化を監視
observer.observe(document.documentElement);

// 念のため window.resize でも補完
window.addEventListener("resize", setViewportWidth);

// tab関連
document.addEventListener("DOMContentLoaded", () => {
  topFvSlider();
  initTopFvCarousel();
  initPickupSlider();
  initCarouselBlocks();
  initOpenCampusSwitcher();
  initOpenCampusCalendar();
  initPieCharts();
  initTabs();
  initLibraryCalendar();
  initVideoLightbox();
});

document.addEventListener("DOMContentLoaded", () => {
  const postcode = document.querySelector('input[name="postcode"]');
  const pref = document.querySelector('input[name="address-pref"]');
  const city = document.querySelector('input[name="address-city"]');
  const street = document.querySelector('input[name="address-street"]');
  const address1 = document.querySelector('input[name="address-1"]');

  if (!postcode || !pref || !city || !street || !address1) return;

  const fillAddress1 = () => {
    const merged = `${pref.value || ""}${city.value || ""}${
      street.value || ""
    }`.trim();
    if (merged) address1.value = merged; // ← これが必要
  };

  // 郵便番号入力後に少し待って結合（yubinbangoの反映待ち）
  postcode.addEventListener("input", () => setTimeout(fillAddress1, 80));
  postcode.addEventListener("change", () => setTimeout(fillAddress1, 80));

  // hidden側が変わった時も追従
  ["input", "change"].forEach((evt) => {
    pref.addEventListener(evt, fillAddress1);
    city.addEventListener(evt, fillAddress1);
    street.addEventListener(evt, fillAddress1);
  });
});

// document.addEventListener('DOMContentLoaded', function () {
//   const deleteButton = document.querySelector('.fixed_nav .line-wrap .delete');

//   if (!deleteButton) return;

//   deleteButton.addEventListener('click', function () {
//     const lineWrap = deleteButton.closest('.line-wrap');

//     if (lineWrap) {
//       lineWrap.classList.add('deactive');
//     }
//   });
// });